import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Briefcase, CheckCircle2, Clock, MapPin, DollarSign, CalendarDays, Loader2, Send, House } from "lucide-react";
import { PageHero, Reveal } from "../components/site/Motion";
import { JobCard, jobMeta, JobStatusNote } from "../components/careers/JobCard";
import { ArrangementFilter } from "../components/careers/ArrangementFilter";
import { SingleFileUpload } from "../components/careers/SingleFileUpload";
import { http, apiError } from "../lib/api";

const useJobs = () => useQuery({ queryKey: ["careers-jobs"], queryFn: async () => (await http.get("/careers/jobs")).data, retry: 1 });
const useJob = (slug) => useQuery({ queryKey: ["careers-job", slug], queryFn: async () => (await http.get(`/careers/jobs/${slug}`)).data, retry: false });

const INTRO = [
  "We are building a team of professionals who are committed to excellence, collaboration, integrity, and outstanding service.",
  "Explore our current opportunities and apply for a position that matches your experience, qualifications, and career goals.",
  "We welcome applications from qualified candidates who are interested in contributing to the growth and success of Selby Asset Group.",
];

export function CareersPage() {
  const { data, isLoading, isError } = useJobs();
  const [arr, setArr] = useState("");
  const all = Array.isArray(data?.items) ? data.items : [];
  const arrangements = Array.isArray(data?.arrangements) ? data.arrangements : [];
  const counts = all.reduce((acc, j) => (j.work_arrangement ? { ...acc, [j.work_arrangement]: (acc[j.work_arrangement] || 0) + 1 } : acc), {});
  const items = arr ? all.filter((j) => j.work_arrangement === arr) : all;
  const departments = (Array.isArray(data?.departments) ? data.departments : []).filter((d) => items.some((j) => j.department === d));
  const others = items.filter((j) => !(data?.departments || []).includes(j.department));
  const groups = [...departments.map((d) => [d, items.filter((j) => j.department === d)]), ...(others.length ? [["Other Opportunities", others]] : [])];
  return (
    <div data-testid="careers-page">
      <PageHero eyebrow="Careers" title={["Career Opportunities", "at Selby Asset Group."]} intro="Join a growing team in asset acquisition, sales, procurement and operations." testid="careers-hero" />
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-24 grid lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-4"><Briefcase className="w-10 h-10 text-gold" /><p className="eyebrow mt-6">Join our team</p></Reveal>
        <div className="lg:col-span-8 space-y-6">
          {INTRO.map((p, i) => <Reveal key={i} delay={i * 0.04}><p className={i === 0 ? "font-display text-3xl md:text-4xl leading-snug text-slate-100" : "text-base md:text-lg text-slate-300 leading-relaxed"}>{p}</p></Reveal>)}
        </div>
      </section>
      <section className="border-t hairline max-w-7xl mx-auto px-5 md:px-10 py-20" data-testid="careers-departments">
        {all.length > 0 && <ArrangementFilter options={arrangements} value={arr} onChange={setArr} counts={counts} total={all.length} />}
        <div className="space-y-20">
        {isLoading ? <Loader2 className="w-6 h-6 animate-spin text-gold" /> : isError ? (
          <p className="text-slate-300" data-testid="careers-error">Job listings couldn't be loaded right now. Please try again shortly.</p>
        ) : !groups.length ? (
          <p className="border border-dashed border-white/20 p-12 text-center text-slate-300" data-testid="careers-empty">{arr ? `No ${arr} positions are listed right now. Try another work arrangement.` : "There are no open positions right now. Please check back soon."}</p>
        ) : groups.map(([dep, jobs]) => (
          <div key={dep} data-testid={`careers-dept-${dep.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="flex items-end justify-between gap-4 mb-8 border-b hairline pb-4">
              <h2 className="font-display text-3xl md:text-4xl text-slate-50">{dep}</h2>
              <span className="text-xs font-mono text-slate-400">{jobs.length} {jobs.length === 1 ? "position" : "positions"}</span>
            </div>
            <div className="grid md:grid-cols-2 gap-6">{jobs.map((j) => <JobCard key={j.id} job={j} />)}</div>
          </div>
        ))}
        </div>
      </section>
    </div>
  );
}

const List = ({ title, items, testid }) => (items?.length ? (
  <section data-testid={testid}>
    <h2 className="font-display text-2xl md:text-3xl text-slate-50 mb-4">{title}</h2>
    <ul className="space-y-2.5">{items.map((x, i) => <li key={i} className="flex gap-3 text-slate-300 leading-relaxed"><span className="mt-2.5 w-1.5 h-1.5 bg-[var(--gold)] shrink-0" />{x}</li>)}</ul>
  </section>
) : null);

const Facts = ({ job }) => {
  const m = jobMeta(job);
  const rows = [[Briefcase, "Department", job.department], [House, "Work arrangement", m.arrangement], [Clock, "Employment type", m.type], [MapPin, "Work location", m.location], [DollarSign, "Compensation", m.pay], [CalendarDays, "Application deadline", m.deadline]].filter((r) => r[2]);
  return (
    <aside className="border hairline bg-[var(--ink-2)] p-6 space-y-4 lg:sticky lg:top-28" data-testid="job-facts">
      {rows.map(([Icon, k, v]) => (
        <div key={k} className="flex gap-3" data-testid={`job-fact-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}><Icon className="w-4 h-4 text-gold mt-0.5 shrink-0" /><div><p className="text-xs font-mono uppercase tracking-wider text-slate-400">{k}</p><p className="text-sm text-slate-100 mt-1">{v}</p></div></div>
      ))}
      {job.accepting ? <Link to={`/careers/${job.slug}/apply`} className="btn-gold w-full justify-center" data-testid="job-detail-apply-button"><Briefcase className="w-4 h-4" /> Apply Now</Link> : <JobStatusNote job={job} />}
    </aside>
  );
};

const NotFound = () => (
  <div className="max-w-3xl mx-auto px-5 py-40 text-center" data-testid="job-not-found">
    <p className="font-display text-4xl">This position isn't available.</p>
    <Link to="/careers" className="btn-ghost mt-8 inline-flex">See all openings</Link>
  </div>
);

export function JobDetailPage() {
  const { slug } = useParams();
  const { data: job, isLoading, isError } = useJob(slug);
  if (isLoading) return <div className="py-48 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>;
  if (isError || !job) return <NotFound />;
  return (
    <div data-testid="job-detail-page">
      <PageHero eyebrow={job.department} title={[job.title]} intro={job.overview} testid="job-hero" />
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-20 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8 space-y-12">
          <Link to="/careers" className="inline-flex items-center gap-2 text-sm text-gold" data-testid="job-back-link"><ArrowLeft className="w-4 h-4" /> All career opportunities</Link>
          <section><h2 className="font-display text-2xl md:text-3xl text-slate-50 mb-4">Job overview</h2><p className="text-slate-300 leading-relaxed">{job.overview}</p></section>
          <List title="Key responsibilities" items={job.responsibilities} testid="job-responsibilities" />
          <List title="Required qualifications" items={job.required_qualifications} testid="job-required" />
          <List title="Preferred qualifications" items={job.preferred_qualifications} testid="job-preferred" />
          {job.skills?.length > 0 && (
            <section data-testid="job-skills"><h2 className="font-display text-2xl md:text-3xl text-slate-50 mb-4">Required skills</h2>
              <div className="flex flex-wrap gap-2">{job.skills.map((s) => <span key={s} className="text-xs px-3 py-1.5 border hairline text-slate-200">{s}</span>)}</div>
            </section>
          )}
        </div>
        <div className="lg:col-span-4"><Facts job={job} /></div>
      </section>
    </div>
  );
}

const EMPTY = { full_name: "", email: "", phone: "", city: "", state: "", country: "", employment_preference: "", linkedin: "", years_experience: "", message: "", consent: false, website: "" };

const F = ({ id, label, req, f, set, type = "text", ph = "" }) => (
  <div>
    <label className="label" htmlFor={`ap-${id}`}>{label}{req ? " *" : <span className="text-slate-400 normal-case tracking-normal"> (optional)</span>}</label>
    <input id={`ap-${id}`} type={type} required={req} className="field" placeholder={ph} value={f[id]} onChange={set(id)} data-testid={`apply-${id}-input`} />
  </div>
);

const Submitted = ({ job, refNo }) => (
  <div className="border border-[rgba(212,175,55,0.35)] p-10 md:p-16 text-center" data-testid="application-success">
    <CheckCircle2 className="w-12 h-12 text-gold mx-auto" />
    <h2 className="font-display text-4xl mt-5">Application Submitted Successfully</h2>
    <p className="font-mono text-sm text-gold mt-3" data-testid="application-success-ref">{job.title}{refNo ? ` · Reference ${refNo}` : ""}</p>
    <div className="text-slate-300 mt-5 max-w-xl mx-auto space-y-3 leading-relaxed">
      <p>Thank you for your interest in joining Selby Asset Group.</p>
      <p>Your application has been received successfully. Our recruitment team will review your submission and contact you if your qualifications match our current employment opportunities.</p>
      <p>We appreciate your interest in becoming part of our team.</p>
    </div>
    <Link to="/careers" className="btn-ghost mt-8 inline-flex" data-testid="application-success-back">Back to Careers</Link>
  </div>
);

const ApplicationForm = ({ job, onDone }) => {
  const [f, setF] = useState(EMPTY);
  const [resume, setResume] = useState(null);
  const [cover, setCover] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!resume) return toast.error("Please upload your resume.");
    if (!f.consent) return toast.error("Please confirm the recruitment consent checkbox.");
    setBusy(true);
    try {
      const { data } = await http.post("/careers/apply", { ...f, job_id: job.id, resume: { id: resume.id, token: resume.token }, cover_letter: cover ? { id: cover.id, token: cover.token } : null });
      onDone(data?.ref || "");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-10" data-testid="application-form">
      <input type="text" tabIndex={-1} autoComplete="off" className="hidden" value={f.website} onChange={set("website")} aria-hidden="true" />
      <div className="border border-[rgba(212,175,55,0.35)] bg-[rgba(212,175,55,0.06)] px-5 py-4" data-testid="apply-position">
        <p className="text-xs font-mono uppercase tracking-wider text-slate-400">Position Applying For</p>
        <p className="font-display text-2xl text-slate-50 mt-1" data-testid="apply-position-title">{job.title}</p>
        <p className="text-xs text-slate-400 mt-1">{[job.department, job.work_arrangement, job.employment_type, job.location].filter(Boolean).join(" · ")}</p>
      </div>
      <fieldset className="grid sm:grid-cols-2 gap-5">
        <legend className="eyebrow mb-5">Applicant information</legend>
        <div className="sm:col-span-2"><F id="full_name" label="Full legal name" req f={f} set={set} /></div>
        <F id="email" label="Email address" req type="email" f={f} set={set} />
        <F id="phone" label="Phone number" req type="tel" f={f} set={set} />
        <F id="city" label="City" req f={f} set={set} />
        <F id="state" label="State" req f={f} set={set} />
        <F id="country" label="Country" req f={f} set={set} />
        <div>
          <label className="label" htmlFor="ap-pref">Employment type preference <span className="text-slate-400 normal-case tracking-normal">(optional)</span></label>
          <select id="ap-pref" className="field" value={f.employment_preference} onChange={set("employment_preference")} data-testid="apply-employment-preference-select">
            <option value="">Select</option>{["Full-time", "Part-time", "Contract", "Other"].map((o) => <option key={o}>{o}</option>)}
          </select>
        </div>
        <F id="linkedin" label="LinkedIn profile" f={f} set={set} ph="https://www.linkedin.com/in/" />
        <F id="years_experience" label="Years of relevant experience" f={f} set={set} />
      </fieldset>
      <fieldset className="grid sm:grid-cols-2 gap-5">
        <legend className="eyebrow mb-5">Resume & supporting documents</legend>
        <SingleFileUpload label="Resume" required value={resume} onChange={setResume} testid="apply-resume" />
        <SingleFileUpload label="Cover letter" value={cover} onChange={setCover} testid="apply-cover" />
        <div className="sm:col-span-2">
          <label className="label" htmlFor="ap-message">Message <span className="text-slate-400 normal-case tracking-normal">(optional)</span></label>
          <textarea id="ap-message" rows={5} className="field" placeholder="Introduce yourself or share anything else we should know." value={f.message} onChange={set("message")} data-testid="apply-message-input" />
        </div>
      </fieldset>
      <label className="flex items-start gap-3 text-sm text-slate-300 cursor-pointer">
        <input type="checkbox" required checked={f.consent} onChange={set("consent")} className="mt-1 accent-[var(--gold)] w-4 h-4" data-testid="apply-consent-checkbox" />
        <span>I agree that the information and documents I submit may be used by Selby Asset Group for recruitment and hiring purposes. *</span>
      </label>
      <div className="flex flex-col sm:flex-row sm:items-center gap-5 border-t hairline pt-8">
        <button className="btn-gold" disabled={busy} data-testid="apply-submit-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Submit Application</button>
        <p className="text-xs text-slate-400 max-w-md">Please don't include your Social Security number, bank details or other financial information. Your documents are stored privately and are only seen by our hiring team.</p>
      </div>
    </form>
  );
};

export function ApplyPage() {
  const { slug } = useParams();
  const { data: job, isLoading, isError } = useJob(slug);
  const [refNo, setRefNo] = useState(null);
  if (isLoading) return <div className="py-48 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>;
  if (isError || !job) return <NotFound />;
  return (
    <div data-testid="apply-page">
      <PageHero eyebrow="Apply" title={[refNo !== null ? "Thank you." : job.title]} intro={refNo !== null ? "Your application is with our hiring team." : `Apply for the ${job.title} position in ${job.department}.`} testid="apply-hero" />
      <section className="max-w-4xl mx-auto px-5 md:px-10 py-16 md:py-20">
        {refNo !== null ? <Submitted job={job} refNo={refNo} /> : !job.accepting ? (
          <div className="border hairline p-10 text-center" data-testid="apply-closed"><p className="font-display text-3xl">This position is no longer accepting applications.</p><Link to="/careers" className="btn-ghost mt-6 inline-flex">See open positions</Link></div>
        ) : (
          <>
            <Link to={`/careers/${job.slug}`} className="inline-flex items-center gap-2 text-sm text-gold mb-8" data-testid="apply-back-link"><ArrowLeft className="w-4 h-4" /> Job description</Link>
            <ApplicationForm job={job} onDone={setRefNo} />
          </>
        )}
      </section>
    </div>
  );
}
